import { memo, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { XIcon } from "@heroicons/react/outline";
import { APP_ROUTES } from "const";

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const features = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    title: "Report Issues",
    description: "Shine a light on problems in your community that need attention",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: "Propose Solutions",
    description: "Share your ideas and expertise to address real challenges",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: "Join Action Groups",
    description: "Connect with like-minded people to turn ideas into impact",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: "Track Your Impact",
    description: "See how your contributions are making a difference globally",
  },
];

function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Trigger animation
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleSignIn = () => {
    onClose();
    setTimeout(() => navigate(APP_ROUTES.SIGN_IN), 100);
  };

  const handleSignUp = () => {
    onClose();
    setTimeout(() => navigate(APP_ROUTES.SIGN_UP), 100);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{
        backgroundColor: isVisible ? "rgba(28, 25, 23, 0.6)" : "rgba(28, 25, 23, 0)",
        backdropFilter: isVisible ? "blur(4px)" : "blur(0px)",
        transition: "background-color 0.3s ease, backdrop-filter 0.3s ease",
      }}
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div
          className="relative w-full max-w-lg rounded-2xl overflow-hidden"
          style={{
            backgroundColor: "#FFFEF9",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            transform: isVisible ? "scale(1) translateY(0)" : "scale(0.95) translateY(10px)",
            opacity: isVisible ? 1 : 0,
            transition: "transform 0.3s ease, opacity 0.3s ease",
            maxHeight: "calc(100vh - 2rem)",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header with gradient accent - sticky */}
        <div
          className="relative px-6 pt-6 pb-4 shrink-0"
          style={{
            background: "linear-gradient(135deg, rgba(194, 65, 12, 0.08) 0%, rgba(194, 65, 12, 0.02) 100%)",
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2.5 rounded-full transition-colors z-10"
            style={{
              color: "#57534E",
              backgroundColor: "rgba(0,0,0,0.05)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.1)";
              e.currentTarget.style.color = "#1C1917";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
              e.currentTarget.style.color = "#57534E";
            }}
          >
            <XIcon className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#C2410C" }}
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2
                className="text-xl font-bold"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  color: "#1C1917",
                }}
              >
                Welcome to WEattitude
              </h2>
            </div>
          </div>
          <p
            className="text-sm"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#57534E",
              lineHeight: 1.6,
            }}
          >
            A global platform where communities come together to identify problems, share solutions, and create lasting change.
          </p>
        </div>

        {/* Features list - scrollable */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <p
            className="text-xs font-semibold uppercase tracking-wider mb-3"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#A8A29E",
            }}
          >
            What you can do
          </p>
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 rounded-xl transition-colors"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                }}
              >
                <div
                  className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: "rgba(194, 65, 12, 0.1)",
                    color: "#C2410C",
                  }}
                >
                  {feature.icon}
                </div>
                <div>
                  <h3
                    className="font-semibold text-sm"
                    style={{
                      fontFamily: "'Source Sans 3', sans-serif",
                      color: "#1C1917",
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-xs"
                    style={{
                      fontFamily: "'Source Sans 3', sans-serif",
                      color: "#78716C",
                      lineHeight: 1.5,
                    }}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with CTAs - sticky */}
        <div
          className="px-6 py-4 flex flex-col sm:flex-row gap-3 shrink-0"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderTop: "1px solid #E7E5E4",
          }}
        >
          <button
            onClick={handleSignUp}
            className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              backgroundColor: "#C2410C",
              boxShadow: "0 4px 12px -2px rgba(194, 65, 12, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#A13508";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 16px -2px rgba(194, 65, 12, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#C2410C";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px -2px rgba(194, 65, 12, 0.3)";
            }}
          >
            Get Started
          </button>
          <button
            onClick={handleSignIn}
            className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              backgroundColor: "transparent",
              color: "#44403C",
              border: "1px solid #D6D3D1",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.03)";
              e.currentTarget.style.borderColor = "#A8A29E";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "#D6D3D1";
            }}
          >
            Sign In
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all sm:hidden"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: "#78716C",
            }}
          >
            Browse First
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default memo(WelcomeModal);
